import { i18n } from 'boot/i18n';
import { aeEmirates, auStates, caProvinces, usStates } from 'boot/countries';

/**
 * What an address looks like in a given country.
 *
 * Whether there is a subdivision at all, what it is called, which ones exist, and what the postal
 * code is called. Four questions with one answer each, all of which vary by country and none of
 * which any feature should decide for itself.
 *
 * A GENERIC SUBDOMAIN, deliberately not a bounded context. It has no language of its own and no
 * invariants to protect; it is a lookup that several unrelated areas need, so it belongs in a lower
 * layer where anything may consume it rather than behind a published interface.
 *
 * Extracted from `mixins/Countries`, which still holds the tax rules and now delegates here for the
 * address ones. Mixins are deprecated, and duplicating this instead of extracting it would leave two
 * copies of a list that only ever changes for external reasons.
 */

const STATES_BY_COUNTRY = {
  US: usStates,
  AU: auStates,
  AE: aeEmirates,
  CA: caProvinces,
};

/** The one country we know of with no postal code, rather than a general rule. */
const COUNTRIES_WITHOUT_POSTAL_CODE = ['AE'];

const STATE_LABEL_KEY_BY_COUNTRY = {
  US: 'state',
  AU: 'state',
  AE: 'emirate',
};

// eslint-disable-next-line import/prefer-default-export
export function useCountries() {
  /** Does an address in this country carry a subdivision at all? */
  const countryHasStates = (country) => Object.hasOwn(STATES_BY_COUNTRY, country ?? '');

  /**
   * The subdivisions, as `{ code, label }`.
   *
   * A CLOSED LIST RATHER THAN FREE TEXT. A payer matches on the code, so "Oregon", "Ore." and "OR"
   * are three ways to fail the same claim.
   */
  const countryStates = (country) => STATES_BY_COUNTRY[country] ?? [];

  /** What that subdivision is called here: a state, a province, an emirate. */
  const countryStateLabel = (country) => {
    return i18n.t(`common.stateLabels.${STATE_LABEL_KEY_BY_COUNTRY[country] ?? 'province'}`);
  };

  const countryHasPostalCode = (country) => !COUNTRIES_WITHOUT_POSTAL_CODE.includes(country);

  /** Zip code where that is what people call it, postal code everywhere else. */
  const countryPostalCodeLabel = (country) => {
    return i18n.t(`common.postalCodeLabels.${country === 'US' ? 'zipCode' : 'postalCode'}`);
  };

  return {
    countryHasStates,
    countryStates,
    countryStateLabel,
    countryHasPostalCode,
    countryPostalCodeLabel,
  };
} // useCountries()
